import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';

// Lazy-load the YouTube IFrame Player API exactly once.
let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') { try { prev(); } catch (_) {} }
      resolve(window.YT);
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.async = true;
    s.onerror = () => reject(new Error('Failed to load YouTube API'));
    document.head.appendChild(s);
  });
  return ytApiPromise;
};

/**
 * YouTubePlayer
 *
 * A thin wrapper around the YouTube IFrame API that mimics our native
 * <video> behavior so the theatre's "auto-advance next, time-update,
 * mute toggle, fullscreen" code keeps working.
 *
 * Props:
 *   videoId          : YouTube 11-char ID (required)
 *   muted            : boolean
 *   autoplay         : boolean (default true)
 *   onEnded          : fires when video ends
 *   onPlay/onPause   : playback state changes
 *   onTimeUpdate     : (currentTime, duration) => void  — fires once a second
 *   onCanPlay        : after player is ready and not buffering
 *   onWaiting        : when buffering
 *
 * Ref methods: play(), pause(), seekTo(seconds), setMuted(bool), getDuration(), getCurrentTime(), enterFullscreen()
 */
const YouTubePlayer = forwardRef(({
  videoId,
  muted = true,
  autoplay = true,
  onEnded,
  onPlay,
  onPause,
  onTimeUpdate,
  onCanPlay,
  onWaiting,
  className = '',
}, ref) => {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Build and destroy player when videoId changes
  useEffect(() => {
    let destroyed = false;
    if (!videoId) return;
    loadYouTubeApi().then((YT) => {
      if (destroyed || !hostRef.current) return;
      try {
        playerRef.current = new YT.Player(hostRef.current, {
          videoId,
          width: '100%',
          height: '100%',
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: autoplay ? 1 : 0,
            mute: muted ? 1 : 0,
            controls: 0,                // we render our own controls
            rel: 0,                     // related videos restricted
            modestbranding: 1,
            playsinline: 1,
            enablejsapi: 1,
            iv_load_policy: 3,          // hide annotations
            origin: window.location.origin,
          },
          events: {
            onReady: () => {
              if (destroyed) return;
              setReady(true);
              if (muted) {
                try { playerRef.current.mute(); } catch (_) {}
              }
              if (autoplay) {
                try { playerRef.current.playVideo(); } catch (_) {}
              }
              if (typeof onCanPlay === 'function') onCanPlay();
              // Start tickling onTimeUpdate
              if (intervalRef.current) clearInterval(intervalRef.current);
              intervalRef.current = setInterval(() => {
                if (!playerRef.current) return;
                try {
                  const t = playerRef.current.getCurrentTime();
                  const d = playerRef.current.getDuration();
                  if (typeof onTimeUpdate === 'function') onTimeUpdate(t || 0, d || 0);
                } catch (_) {}
              }, 500);
            },
            onStateChange: (e) => {
              const S = window.YT.PlayerState;
              if (e.data === S.PLAYING) {
                if (typeof onPlay === 'function') onPlay();
                if (typeof onCanPlay === 'function') onCanPlay();
              } else if (e.data === S.PAUSED) {
                if (typeof onPause === 'function') onPause();
              } else if (e.data === S.ENDED) {
                if (typeof onEnded === 'function') onEnded();
              } else if (e.data === S.BUFFERING) {
                if (typeof onWaiting === 'function') onWaiting();
              }
            },
            onError: () => {
              // Treat any player error like "ended" so the playlist advances.
              if (typeof onEnded === 'function') onEnded();
            },
          },
        });
      } catch (e) {
        console.error('YouTube player init failed', e);
      }
    }).catch((e) => console.error(e));

    return () => {
      destroyed = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try { playerRef.current.destroy(); } catch (_) {}
      }
      playerRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Sync mute prop changes
  useEffect(() => {
    if (!ready || !playerRef.current) return;
    try {
      if (muted) playerRef.current.mute(); else playerRef.current.unMute();
    } catch (_) {}
  }, [muted, ready]);

  useImperativeHandle(ref, () => ({
    play: () => { try { playerRef.current?.playVideo(); } catch (_) {} },
    pause: () => { try { playerRef.current?.pauseVideo(); } catch (_) {} },
    seekTo: (seconds) => { try { playerRef.current?.seekTo(seconds, true); } catch (_) {} },
    setMuted: (m) => { try { m ? playerRef.current?.mute() : playerRef.current?.unMute(); } catch (_) {} },
    getDuration: () => { try { return playerRef.current?.getDuration() || 0; } catch (_) { return 0; } },
    getCurrentTime: () => { try { return playerRef.current?.getCurrentTime() || 0; } catch (_) { return 0; } },
    enterFullscreen: () => {
      const iframe = hostRef.current?.tagName === 'IFRAME' ? hostRef.current : hostRef.current?.querySelector('iframe');
      if (!iframe) return;
      const req = iframe.requestFullscreen || iframe.webkitRequestFullscreen || iframe.mozRequestFullScreen || iframe.msRequestFullscreen;
      try { if (req) req.call(iframe); } catch (_) {}
    },
  }), []);

  return (
    <div className={`w-full h-full bg-black ${className}`} data-testid="youtube-player">
      <div ref={hostRef} className="w-full h-full" />
    </div>
  );
});

YouTubePlayer.displayName = 'YouTubePlayer';

export default YouTubePlayer;
